import { useEffect, useState } from "react";
import { Repeat, CheckCircle2, XCircle, Loader2, Clock, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import USDateInput from "./USDateInput";

/**
 * Counter-offer inbox shown above the Leases table. Lists every renewal
 * proposal currently in `status="countered"` and lets the landlord:
 *   - Accept the tenant's counter terms (creates the renewed lease)
 *   - Decline the counter (ends the negotiation)
 *   - Re-counter with new terms (flips proposal back to `pending`; tenant can
 *     then accept/decline/counter again on the same shareable URL)
 *
 * Calls:
 *   GET  /api/lease-renewal-proposals?status=countered
 *   POST /api/leases/{lid}/renewal-proposals/{pid}/counter-decision
 */
export default function CounterOfferInbox({ onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submittingId, setSubmittingId] = useState(null);
  const [recounterFor, setRecounterFor] = useState(null);
  const [recounterRent, setRecounterRent] = useState("");
  const [recounterEnd, setRecounterEnd] = useState("");
  const [recounterDeposit, setRecounterDeposit] = useState("");
  const [recounterMessage, setRecounterMessage] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/lease-renewal-proposals", { params: { status: "countered" } });
      setItems(r.data || []);
    } catch {
      // non-fatal — banner just hides itself
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startRecounter = (item) => {
    setRecounterFor(item.id);
    const c = item.tenant_counter || {};
    setRecounterRent(String(c.monthly_rent ?? item.monthly_rent ?? ""));
    setRecounterEnd(c.end_date || item.end_date || "");
    setRecounterDeposit(String(c.deposit ?? item.deposit ?? ""));
    setRecounterMessage("");
  };

  const submitDecision = async (item, decision, payload = {}) => {
    setSubmittingId(item.id);
    try {
      const body = { decision, ...payload };
      const r = await api.post(
        `/leases/${item.lease_id}/renewal-proposals/${item.id}/counter-decision`,
        body,
      );
      toast.success(
        decision === "accept" ? "Counter accepted — new lease created" :
        decision === "decline" ? "Counter declined" :
        "New offer sent to tenant",
      );
      // Optimistically remove this row (it's no longer countered)
      setItems((prev) => prev.filter((p) => p.id !== item.id));
      setRecounterFor(null);
      onChanged?.(r.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Decision failed");
    } finally {
      setSubmittingId(null);
    }
  };

  if (loading) return null;
  if (items.length === 0) return null;

  return (
    <div className="bg-amber-50/40 border border-amber-300 rounded-sm" data-testid="counter-offer-inbox">
      <div className="px-5 py-3 border-b border-amber-200 flex items-center gap-2">
        <Repeat className="w-4 h-4 text-amber-700" />
        <div className="font-semibold text-amber-900" style={{ fontFamily: "Outfit" }}>
          {items.length} tenant counter-offer{items.length === 1 ? "" : "s"} awaiting your decision
        </div>
      </div>
      <div className="divide-y divide-amber-100">
        {items.map((item) => {
          const c = item.tenant_counter || {};
          const isOpen = recounterFor === item.id;
          return (
            <div key={item.id} className="px-5 py-4" data-testid={`counter-row-${item.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="font-semibold text-slate-900">
                    {item.property_name} · {item.unit_label || "Unit"}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {item.tenant_name} · {item.tenant_email}
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <Clock className="w-3 h-3 text-amber-700" />
                  <span className="text-amber-800 font-mono">
                    Countered {c.submitted_at ? new Date(c.submitted_at).toLocaleString() : "recently"}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3 text-sm">
                <DiffCell label="Monthly rent" original={item.monthly_rent} counter={c.monthly_rent} format="usd" />
                <DiffCell label="End date" original={item.end_date} counter={c.end_date} />
                <DiffCell label="Deposit" original={item.deposit} counter={c.deposit} format="usd" />
              </div>

              {c.tenant_message ? (
                <div className="mt-3 bg-white border border-amber-200 rounded-sm p-3 text-xs text-slate-700 flex gap-2">
                  <MessageSquare className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-700" />
                  <div className="whitespace-pre-line italic" data-testid={`counter-message-${item.id}`}>
                    "{c.tenant_message}"
                  </div>
                </div>
              ) : null}

              {isOpen ? (
                <div className="mt-4 bg-white border border-amber-300 rounded-sm p-4 space-y-3" data-testid={`recounter-form-${item.id}`}>
                  <div className="text-[11px] uppercase tracking-wider text-amber-900 font-bold">Re-counter terms</div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <label className="block">
                      <span className="text-[10px] uppercase tracking-wider text-slate-600">Monthly rent</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="input-clean w-full font-mono"
                        value={recounterRent}
                        onChange={(e) => setRecounterRent(e.target.value)}
                        data-testid={`recounter-rent-${item.id}`}
                      />
                    </label>
                    <label className="block">
                      <span className="text-[10px] uppercase tracking-wider text-slate-600">End date</span>
                      <USDateInput
                        className="input-clean w-full font-mono"
                        value={recounterEnd}
                        onChange={(iso) => setRecounterEnd(iso)}
                        data-testid={`recounter-end-${item.id}`}
                      />
                    </label>
                    <label className="block">
                      <span className="text-[10px] uppercase tracking-wider text-slate-600">Deposit</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="input-clean w-full font-mono"
                        value={recounterDeposit}
                        onChange={(e) => setRecounterDeposit(e.target.value)}
                        data-testid={`recounter-deposit-${item.id}`}
                      />
                    </label>
                  </div>
                  <textarea
                    rows={2}
                    className="input-clean w-full text-sm"
                    placeholder="Optional message to your tenant…"
                    maxLength={1000}
                    value={recounterMessage}
                    onChange={(e) => setRecounterMessage(e.target.value)}
                    data-testid={`recounter-message-${item.id}`}
                  />
                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={() => submitDecision(item, "recounter", {
                        monthly_rent: Number(recounterRent),
                        end_date: recounterEnd,
                        deposit: Number(recounterDeposit),
                        landlord_message: recounterMessage,
                      })}
                      disabled={submittingId === item.id}
                      className="btn-primary-dark inline-flex items-center gap-2"
                      data-testid={`recounter-submit-${item.id}`}
                    >
                      {submittingId === item.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Repeat className="w-4 h-4" />}
                      Send re-counter
                    </button>
                    <button
                      onClick={() => setRecounterFor(null)}
                      disabled={submittingId === item.id}
                      className="btn-outline-dark"
                      data-testid={`recounter-cancel-${item.id}`}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2 mt-4 flex-wrap">
                  <button
                    onClick={() => submitDecision(item, "accept")}
                    disabled={submittingId === item.id}
                    className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white px-3 py-1.5 text-xs font-bold rounded-sm transition-colors"
                    data-testid={`counter-accept-${item.id}`}
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" /> Accept counter
                  </button>
                  <button
                    onClick={() => startRecounter(item)}
                    disabled={submittingId === item.id}
                    className="inline-flex items-center gap-1.5 bg-amber-100 hover:bg-amber-200 border border-amber-300 text-amber-900 px-3 py-1.5 text-xs font-bold rounded-sm transition-colors"
                    data-testid={`counter-recounter-${item.id}`}
                  >
                    <Repeat className="w-3.5 h-3.5" /> Re-counter
                  </button>
                  <button
                    onClick={() => submitDecision(item, "decline")}
                    disabled={submittingId === item.id}
                    className="inline-flex items-center gap-1.5 border border-slate-300 hover:border-slate-500 text-slate-700 px-3 py-1.5 text-xs font-bold rounded-sm transition-colors"
                    data-testid={`counter-decline-${item.id}`}
                  >
                    <XCircle className="w-3.5 h-3.5" /> Decline
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DiffCell({ label, original, counter, format }) {
  const orig = format === "usd" ? `$${Number(original || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : (original ?? "—");
  const cnt = format === "usd" ? `$${Number(counter || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : (counter ?? "—");
  const changed = String(orig) !== String(cnt);
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-2.5">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <span className={`font-mono ${changed ? "line-through text-slate-400" : "text-slate-900 font-bold"}`}>{orig}</span>
        {changed ? (
          <>
            <span className="text-slate-300">→</span>
            <span className="font-mono font-bold text-amber-800">{cnt}</span>
          </>
        ) : null}
      </div>
    </div>
  );
}
